import { API_BASE, apiOrigin } from "../api/config";

const listeners = new Set();
let socket = null;
let reconnectTimer = null;
let reconnectAttempts = 0;
let manuallyClosed = false;
let authExpired = false;
let connecting = false;
let connectionId = 0;

function socketUrl(ticket) {
  const origin = apiOrigin() || window.location.origin;
  const websocketOrigin = origin.replace(/^http:/i, "ws:").replace(/^https:/i, "wss:");
  return `${websocketOrigin.replace(/\/$/, "")}/ws?client=admin&ticket=${encodeURIComponent(ticket)}`;
}

async function fetchTicket(token) {
  const response = await window.fetch(`${API_BASE.replace(/\/$/, "")}/realtime/ticket`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
  });
  if (response.status === 401) {
    const error = new Error("unauthorized");
    error.authExpired = true;
    throw error;
  }
  const body = await response.json().catch(() => null);
  if (!response.ok || !body || body.code !== 0 || !body.data || !body.data.ticket) throw new Error("ticket failed");
  return body.data.ticket;
}

function handleAuthExpired() {
  authExpired = true;
  if (reconnectTimer) window.clearTimeout(reconnectTimer);
  reconnectTimer = null;
  emit({ event: "realtime.auth_expired", data: {} });
  window.dispatchEvent(new CustomEvent("admin-auth-expired"));
}

function emit(payload) {
  listeners.forEach((listener) => {
    try { listener(payload); } catch (error) { /* 单个页面异常不影响其他订阅者。 */ }
  });
}

function scheduleReconnect() {
  if (manuallyClosed || authExpired || reconnectTimer) return;
  const delay = Math.min(1000 * (2 ** reconnectAttempts), 15000);
  reconnectAttempts += 1;
  reconnectTimer = window.setTimeout(() => {
    reconnectTimer = null;
    connect();
  }, delay);
}

export async function connect() {
  const token = window.localStorage.getItem("admin_token");
  if (!token || connecting || (socket && [WebSocket.OPEN, WebSocket.CONNECTING].includes(socket.readyState))) return;
  manuallyClosed = false;
  authExpired = false;
  connecting = true;
  const currentId = ++connectionId;
  let ticket;
  try {
    ticket = await fetchTicket(token);
  } catch (error) {
    connecting = false;
    if (currentId !== connectionId || manuallyClosed) return;
    if (error.authExpired) handleAuthExpired();
    else scheduleReconnect();
    return;
  }
  connecting = false;
  if (currentId !== connectionId || manuallyClosed) return;
  try {
    socket = new WebSocket(socketUrl(ticket), ["realtime-v1"]);
  } catch (error) {
    scheduleReconnect();
    return;
  }
  socket.onopen = () => {
    reconnectAttempts = 0;
    emit({ event: "realtime.connected", data: {} });
  };
  socket.onmessage = (event) => {
    try { emit(JSON.parse(event.data)); } catch (error) { /* 忽略非 JSON 心跳或异常帧。 */ }
  };
  socket.onerror = () => {};
  socket.onclose = () => {
    socket = null;
    emit({ event: "realtime.disconnected", data: {} });
    scheduleReconnect();
  };
}

export function disconnect() {
  manuallyClosed = true;
  connecting = false;
  connectionId += 1;
  if (reconnectTimer) window.clearTimeout(reconnectTimer);
  reconnectTimer = null;
  reconnectAttempts = 0;
  if (socket) socket.close(1000, "page closed");
  socket = null;
}

export function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
